import React, { useState, Fragment } from 'react';
import { Case, SortConfig } from '../types';
import { ArrowDownIcon, ArrowUpIcon, ChevronRightIcon, SearchXIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface CasesTableProps {
  cases: Case[];
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onToggleSelectAll: () => void;
  sortConfig: SortConfig | null;
  onSort: (key: keyof Case) => void;
}
export function CasesTable({
  cases,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  sortConfig,
  onSort
}: CasesTableProps) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const toggleRow = (id: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };
  const allSelected = cases.length > 0 && cases.every(c => selectedIds.has(c.id));
  const someSelected = cases.length > 0 && cases.some(c => selectedIds.has(c.id)) && !allSelected;
  const formatDate = (isoString: string) => {
    const date = new Date(isoString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    }).format(date);
  };
  const SortIcon = ({
    columnKey
  }: {
    columnKey: keyof Case;
  }) => {
    if (sortConfig?.key !== columnKey) {
      return <ArrowDownIcon className="w-3 h-3 ml-1 inline text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-16" />;
    }
    return sortConfig.direction === 'asc' ? <ArrowUpIcon className="w-3 h-3 ml-1 inline text-cyan-600" data-id="element-17" /> : <ArrowDownIcon className="w-3 h-3 ml-1 inline text-cyan-600" data-id="element-18" />;
  };
  return <div className="overflow-x-auto" data-id="element-19">
      <table className="min-w-full divide-y divide-slate-200" data-id="element-20">
        <thead className="bg-slate-50/80" data-id="element-21">
          <tr data-id="element-22">
            <th scope="col" className="pl-5 pr-2 py-3 text-left w-10" data-id="element-23">
              <input type="checkbox" className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-slate-300 rounded cursor-pointer accent-cyan-600" checked={allSelected} ref={input => {
              if (input) input.indeterminate = someSelected;
            }} onChange={onToggleSelectAll} aria-label="Select all cases" data-id="element-24" />
            </th>
            <th scope="col" className="w-8 px-1 py-3" data-id="element-25">
              <span className="sr-only" data-id="element-26">Expand</span>
            </th>
            <th scope="col" className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider" data-id="element-27">
              Subject
            </th>
            <th scope="col" className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 transition-colors group whitespace-nowrap" onClick={() => onSort('createdOn')} data-id="element-28">
              Created On <SortIcon columnKey="createdOn" data-id="element-29" />
            </th>
            <th scope="col" className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 transition-colors group whitespace-nowrap" onClick={() => onSort('deletedOn')} data-id="element-30">
              Deleted On <SortIcon columnKey="deletedOn" data-id="element-31" />
            </th>
            <th scope="col" className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider hidden sm:table-cell whitespace-nowrap" data-id="element-32">
              Owned By
            </th>
            <th scope="col" className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell whitespace-nowrap" data-id="element-33">
              Deleted By
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-slate-100" data-id="element-34">
          <AnimatePresence initial={false} data-id="element-35">
            {cases.map(c => {
            const isSelected = selectedIds.has(c.id);
            const isExpanded = expandedRows.has(c.id);
            return <Fragment key={c.id} data-id="element-36">
                  <motion.tr initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0,
                height: 0,
                overflow: 'hidden'
              }} transition={{
                duration: 0.2
              }} className={`transition-colors cursor-pointer ${isSelected ? 'bg-cyan-50/60 hover:bg-cyan-50/80' : 'bg-white hover:bg-slate-50/80'}`} onClick={() => toggleRow(c.id)} data-id="element-37">
                    <td className="pl-5 pr-2 py-3.5 whitespace-nowrap" onClick={e => e.stopPropagation()} data-id="element-38">
                      <input type="checkbox" className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-slate-300 rounded cursor-pointer accent-cyan-600" checked={isSelected} onChange={() => onToggleSelect(c.id)} aria-label={`Select case ${c.subject}`} data-id="element-39" />
                    </td>
                    <td className="px-1 py-3.5 whitespace-nowrap" data-id="element-40">
                      <div className={`p-0.5 rounded transition-colors ${isExpanded ? 'text-cyan-600' : 'text-slate-400'}`} data-id="element-41">
                        <motion.div animate={{
                      rotate: isExpanded ? 90 : 0
                    }} transition={{
                      duration: 0.15,
                      ease: 'easeOut'
                    }} data-id="element-42">
                          <ChevronRightIcon className="w-4 h-4" data-id="element-43" />
                        </motion.div>
                      </div>
                    </td>
                    <td className="px-4 py-3.5" data-id="element-44">
                      <span className="text-sm font-semibold text-slate-900 leading-snug" data-id="element-45">
                        {c.subject}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 whitespace-nowrap" data-id="element-46">
                      <span className="text-sm text-slate-500 tabular-nums" data-id="element-47">
                        {formatDate(c.createdOn)}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 whitespace-nowrap" data-id="element-48">
                      <span className="text-sm font-medium text-slate-700 tabular-nums" data-id="element-49">
                        {formatDate(c.deletedOn)}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 whitespace-nowrap hidden sm:table-cell" data-id="element-50">
                      <span className="text-sm text-slate-500" data-id="element-51">
                        {c.ownedBy}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 whitespace-nowrap hidden lg:table-cell" data-id="element-52">
                      <span className="text-sm text-slate-500" data-id="element-53">
                        {c.deletedBy}
                      </span>
                    </td>
                  </motion.tr>

                  {/* Expanded description row */}
                  <AnimatePresence initial={false} data-id="element-54">
                    {isExpanded && <motion.tr key={`${c.id}-desc`} initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.2,
                  ease: 'easeInOut'
                }} data-id="element-55">
                        <td colSpan={7} className="p-0 overflow-hidden border-b border-slate-100" data-id="element-56">
                          <motion.div initial={{
                      opacity: 0,
                      y: -4
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} exit={{
                      opacity: 0,
                      y: -4
                    }} transition={{
                      duration: 0.15,
                      delay: 0.05
                    }} className="bg-slate-50/60" data-id="element-57">
                            <div className="ml-[4.25rem] mr-4 py-3 pl-4 border-l-2 border-cyan-500/40" data-id="element-58">
                              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1" data-id="element-59">
                                Description
                              </p>
                              <p className="text-sm text-slate-600 leading-relaxed max-w-2xl" data-id="element-60">
                                {c.description}
                              </p>
                            </div>
                          </motion.div>
                        </td>
                      </motion.tr>}
                  </AnimatePresence>
                </Fragment>;
          })}
          </AnimatePresence>
        </tbody>
      </table>
    </div>;
}